import { useState, useEffect } from 'react';
import type { MouseEvent } from 'react';
import Link from 'next/link';
import { ThemeRoot, ThemeContext } from '@/components/microstory/theme';
import { ComposableStyledComponentProps } from '@/types/composable-styled-component';
import { styled } from '@/styles/stitches.config';
import { Story } from '@/components/overview';
import { useIsomorphicLayoutEffect } from 'framer-motion';
import { Trans } from '@lingui/macro';

export type CardProps = ComposableStyledComponentProps<HTMLAnchorElement> &
  Story & {
    handleFocus?: () => void;
    disableLink?: boolean;
    animateIn?: boolean;
    animateOut?: boolean;
    png?: boolean;
  };

const Anchor = styled(Link, {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  textDecoration: 'none',
});

const StyledThemeRoot = styled(ThemeRoot, {
  flexGrow: 1,
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingInline: '$5',
  borderRadius: '$space$5',
  overflow: 'hidden',
  backgroundColor: 'var(--color-background)',
  textDecoration: 'none',

  '@media (hover: hover)': {
    '&:hover': {
      '> div.background': {
        transform: 'scale(1.25)',
      },
    },
  },
});

const Background = styled('div', {
  position: 'absolute',
  insetBlockStart: 0,
  insetInlineStart: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  transitionProperty: 'opacity, transform',
  transitionTimingFunction: 'ease-in-out, ease-out',
  variants: {
    hidden: {
      true: {
        opacity: 0,
      },
      false: {
        opacity: 1,
      },
    },
    animateIn: {
      true: {
        transitionDuration: '750ms, 350ms',
      },
      false: {
        transitionDuration: '350ms, 350ms',
      },
    },
  },
});

const ThemeLabel = styled('span', {
  position: 'absolute',
  insetBlockStart: 0,
  marginBlockStart: '$3',
  color: '$white',
  textTransform: 'uppercase',
  fontSize: '0.525em',
  transitionProperty: 'opacity',
  transitionTimingFunction: 'ease-in-out',
  variants: {
    hidden: {
      true: {
        opacity: 0,
      },
      false: {
        opacity: 1,
      },
    },
    animateIn: {
      true: {
        transitionDuration: '750ms',
      },
      false: {
        transitionDuration: '350ms',
      },
    },
  },
});

const TitleLabel = styled('span', {
  position: 'relative',
  fontSize: '1.5em',
  color: '$white',
  textAlign: 'center',
  lineHeight: '$2',
  transitionProperty: 'opacity',
  transitionTimingFunction: 'ease-in-out',

  ':is([lang=en], [lang=es], [lang=fr], [lang=in], [lang=pt], [lang=sw]) &': {
    textTransform: 'lowercase',
    wordBreak: 'keep-all',

    '&:first-letter': {
      textTransform: 'uppercase',
    },
  },

  '[lang=ta] &, [lang=ar] &': {
    fontSize: '1em',
  },

  variants: {
    smallText: {
      true: {
        fontSize: '1em',
      },
    },
    hidden: {
      true: {
        opacity: 0,
      },
      false: {
        opacity: 1,
      },
    },
    animateIn: {
      true: {
        transitionDuration: '750ms',
      },
      false: {
        transitionDuration: '350ms',
      },
    },
  },
});

const StatusLabel = styled('div', {
  position: 'absolute',
  top: '-3em',
  left: '50%',
  height: '2.4em',
  transform: 'translate(-50%, 0)',
  paddingInline: '$3',
  fontSize: '0.350em',
  lineHeight: '2.5',
  textTransform: 'uppercase',
  background: '$white',
  color: '$black',
  borderRadius: '$4',
  whiteSpace: 'nowrap',
});

const noop = () => {
  /* noop */
};
const preventDefault = (e: MouseEvent) => e.preventDefault();

export function Card(props: CardProps) {
  const {
    href,
    title,
    theme,
    themeLabel,
    handleFocus = noop,
    disableLink = false,
    animateIn = false,
    animateOut = false,
    png = false,
    css,
    id,
    status,
  } = props;
  // need to wrap ThemeRoot inside of <a> instead of using the "as" prop as the latter leads to TS issues

  const [hideContent, setHideContent] = useState(animateIn || animateOut);

  useIsomorphicLayoutEffect(() => {
    if (animateOut) {
      setHideContent(true);
    }
  }, [animateOut]);

  const [backgroundGraphicUrl, setBackgroundGraphicUrl] = useState('');
  useEffect(() => {
    let cancelled = false;

    const ext = png ? 'png' : 'svg';

    import(`../../assets/${ext}/story-background/${id}-small.${ext}`)
      .then((module) => {
        if (!cancelled) {
          if (png) {
            setBackgroundGraphicUrl(module.default.src);
          } else {
            setBackgroundGraphicUrl(module.default);
          }
        }
      })
      .catch(noop);

    return () => {
      cancelled = true;
    };
  }, [id, png]);

  // the delay here is in conjunction with overview intro animation
  // it's still a one-off so imo fine to keep it hard-coded but maybe we want to make it configurable
  useEffect(() => {
    if (animateIn) {
      const id = setTimeout(() => {
        setHideContent(false);
      }, 1250);

      return () => {
        clearTimeout(id);
      };
    }
  }, [animateIn]);

  return (
    <Anchor
      css={css}
      onClick={disableLink ? preventDefault : noop}
      onFocus={handleFocus}
      href={href}
    >
      <ThemeContext.Provider value={theme}>
        <StyledThemeRoot theme={theme}>
          <Background
            className="background"
            animateIn={animateIn}
            hidden={hideContent}
            css={{
              backgroundImage: `url(${backgroundGraphicUrl})`,
            }}
          />
          <ThemeLabel animateIn={animateIn} hidden={hideContent}>
            {themeLabel}
          </ThemeLabel>
          <TitleLabel animateIn={animateIn} hidden={hideContent} smallText={title.length > 30}>
            {title}

            {status && (
              <StatusLabel>
                {status === 'NEW' && <Trans id="common.story-status-new">new</Trans>}
              </StatusLabel>
            )}
          </TitleLabel>
        </StyledThemeRoot>
      </ThemeContext.Provider>
    </Anchor>
  );
}
